<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import OptionSelect from 'dashboard/components-next/polaris/cards/OptionSelect.vue';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from 'dashboard/components-next/polaris/ui/field';
import { Input } from 'dashboard/components-next/polaris/ui/input';
import AccountId from './components/AccountId.vue';
import { filterSupportedLanguages } from 'shared/constants/supportedLanguages';
import BuildInfo from './components/BuildInfo.vue';
import AccountDelete from './components/AccountDelete.vue';
import AudioTranscription from './components/AudioTranscription.vue';
import SectionLayout from './components/SectionLayout.vue';

// Phase 9.4 (the kit's SettingsSection, R2-9-1 = C): "Update settings" moves to the page header, the one place the
// kit saves from, and stays off until something changed; audio transcription keeps saving when it is switched.
// Same fields, validation and store action as before.
const { t, locale: appLocale } = useI18n();
const store = useStore();
const { uiSettings } = useUISettings();
const { enabledLanguages } = useConfig();
const { accountId } = useAccount();

const getAccount = useMapGetter('accounts/getAccount');
const uiFlags = useMapGetter('accounts/getUIFlags');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const isOnChatwootCloud = useMapGetter('globalConfig/isOnChatwootCloud');

const id = ref('');
const name = ref('');
const locale = ref('en');
const domain = ref('');
const supportEmail = ref('');
const features = ref({});
const saved = ref('');

const v$ = useVuelidate(
  { name: { required }, locale: { required } },
  {
    name,
    locale,
  }
);

const currentAccount = computed(() => getAccount.value(accountId.value) || {});
const showAudioTranscriptionConfig = computed(() =>
  isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.POLARIS)
);
const languageOptions = computed(() =>
  filterSupportedLanguages(enabledLanguages)
    .sort((l1, l2) => l1.iso_639_1_code.localeCompare(l2.iso_639_1_code))
    .map(lang => ({ value: lang.iso_639_1_code, label: lang.name }))
);
const featureInboundEmailEnabled = computed(
  () => !!features.value?.inbound_emails
);
const featureCustomReplyDomainEnabled = computed(
  () => featureInboundEmailEnabled.value && !!features.value.custom_reply_domain
);
const featureCustomReplyEmailEnabled = computed(
  () => featureInboundEmailEnabled.value && !!features.value.custom_reply_email
);

const formJSON = () =>
  JSON.stringify([name.value, locale.value, domain.value, supportEmail.value]);
const dirty = computed(() => !!saved.value && formJSON() !== saved.value);

const setAppLocale = value => {
  if (value) appLocale.value = value;
};

const initializeAccount = () => {
  const account = getAccount.value(accountId.value);
  if (!account) return;
  setAppLocale(uiSettings.value?.locale || account.locale);
  name.value = account.name;
  locale.value = account.locale;
  id.value = account.id;
  domain.value = account.domain;
  supportEmail.value = account.support_email;
  features.value = account.features;
  saved.value = formJSON();
};

watch(
  () => currentAccount.value.id,
  value => {
    if (value) initializeAccount();
  }
);
onMounted(() => {
  // Account already in the store (navigated in): seed immediately.
  if (currentAccount.value.id) initializeAccount();
});

const updateAccount = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    useAlert(t('GENERAL_SETTINGS.FORM.ERROR'));
    return;
  }
  try {
    await store.dispatch('accounts/update', {
      locale: locale.value,
      name: name.value,
      domain: domain.value,
      support_email: supportEmail.value,
    });
    // If user locale is set, update the locale with user locale
    setAppLocale(uiSettings.value?.locale || locale.value);
    getAccount.value(id.value).locale = locale.value;
    saved.value = formJSON();
    useAlert(t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.UPDATE.ERROR'));
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetchingItem"
    :loading-message="$t('GENERAL_SETTINGS.TITLE')"
  >
    <template #header>
      <BaseSettingsHeader :title="$t('GENERAL_SETTINGS.TITLE')">
        <template #actions>
          <NextButton
            size="sm"
            :disabled="!dirty"
            :is-loading="uiFlags.isUpdating"
            :label="$t('GENERAL_SETTINGS.SUBMIT')"
            @click="updateAccount"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <SectionLayout
        :title="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.TITLE')"
        :description="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.NOTE')"
      >
        <form class="grid gap-5" @submit.prevent="updateAccount">
          <Field :data-invalid="v$.name.$error || undefined">
            <FieldLabel for="account-name">
              {{ $t('GENERAL_SETTINGS.FORM.NAME.LABEL') }}
            </FieldLabel>
            <Input
              id="account-name"
              v-model="name"
              :placeholder="$t('GENERAL_SETTINGS.FORM.NAME.PLACEHOLDER')"
              :aria-invalid="v$.name.$error || undefined"
              @blur="v$.name.$touch"
            />
            <FieldError v-if="v$.name.$error">
              {{ $t('GENERAL_SETTINGS.FORM.NAME.ERROR') }}
            </FieldError>
          </Field>
          <Field>
            <FieldLabel for="account-language">
              {{ $t('GENERAL_SETTINGS.FORM.LANGUAGE.LABEL') }}
            </FieldLabel>
            <OptionSelect
              id="account-language"
              v-model="locale"
              :options="languageOptions"
            />
          </Field>
          <Field v-if="featureCustomReplyDomainEnabled">
            <FieldLabel for="account-domain">
              {{ $t('GENERAL_SETTINGS.FORM.DOMAIN.LABEL') }}
            </FieldLabel>
            <Input
              id="account-domain"
              v-model="domain"
              :placeholder="$t('GENERAL_SETTINGS.FORM.DOMAIN.PLACEHOLDER')"
            />
            <FieldDescription>
              {{
                featureInboundEmailEnabled &&
                $t('GENERAL_SETTINGS.FORM.FEATURES.INBOUND_EMAIL_ENABLED')
              }}
              {{
                featureCustomReplyDomainEnabled &&
                $t('GENERAL_SETTINGS.FORM.FEATURES.CUSTOM_EMAIL_DOMAIN_ENABLED')
              }}
            </FieldDescription>
          </Field>
          <Field v-if="featureCustomReplyEmailEnabled">
            <FieldLabel for="account-support-email">
              {{ $t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.LABEL') }}
            </FieldLabel>
            <Input
              id="account-support-email"
              v-model="supportEmail"
              :placeholder="
                $t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.PLACEHOLDER')
              "
            />
          </Field>
        </form>
      </SectionLayout>
      <AudioTranscription v-if="showAudioTranscriptionConfig" />
      <AccountId />
      <AccountDelete v-if="isOnChatwootCloud" />
      <BuildInfo />
    </template>
  </SettingsLayout>
</template>
